import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ModernFamlyProvider } from '@famly/mf_system_provider';
import setupI18next from 'signin-app/setup/i18next-setup';
import setupLocale from 'signin-app/setup/locale-setup';
import PlatformHelper from 'web-app/helpers/platform-helper';
import LegacyModalStyle from 'web-app/react/global-styles/legacy-modal-style';
import { ModalDialogProvider } from '@famly/mf_modals-dialogs_context';
import GlobalCSS from 'shared/components/global-css';
import GlobalEvent from 'signin-app/global-event/global-event';
import Routes from 'signin-app/routes/routes';
import { AppContainer } from 'signin-app/components/app-container';
import * as PrivateRouteActions from 'signin-app/components/private-route/actions';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import * as LoginSelectors from 'signin-app/login/selectors';
import * as PinSelectors from 'signin-app/pin/selectors';
import * as ChildCheckInSelectors from 'signin-app/child/check-in/selectors';
import RoutesMap from 'signin-app/routes/routes-map';
import { useInterval } from '@famly/vet_ts-utils_generic-hooks';
import { AuthStateProvider } from 'web-app/react/contexts/auth-state';
import currentTheme from 'web-app/styleguide/themes';
import { MFFonts } from 'shared/components/mf-fonts';
import { getModernFamlyLanguage } from 'web-app/setup/locale-setup';
import { ModalContext } from 'web-app/react/modal-context/modal-context';
import { PortalRootId } from 'web-app/react/components/portals/constants';
import { useReloadOnNewRelease } from './use-reload-on-new-release';
const MINUTE = 60 * 1000;
export const App = () => {
    const [setupReady, setSetupReady] = React.useState(false);
    const [locale, setLocale] = React.useState(undefined);
    const dispatch = useDispatch();
    const accessToken = useTypedSelector(LoginSelectors.accessToken);
    const isPinApp = useTypedSelector(LoginSelectors.isPinApp);
    const isLoggedIn = useTypedSelector(LoginSelectors.isLoggedIn);
    const didInsertPin = useTypedSelector(PinSelectors.didInsertPin);
    const isCheckingIn = useTypedSelector(ChildCheckInSelectors.isCheckingIn);
    const addingNewRelation = useTypedSelector(ChildCheckInSelectors.addingNewRelation);
    const isOnGuestFlow = useLocation().pathname.startsWith(RoutesMap.guest);
    const theme = React.useMemo(() => {
        return currentTheme();
    }, []);
    React.useEffect(() => {
        // Apple release note:
        // To improve accessibility on websites in Safari,
        // users can now pinch-to-zoom even when a website sets user-scalable=no in the viewport.
        if (PlatformHelper.isSafariMobile()) {
            disableTouchZoom();
            disableDoubleClickZoom();
        }
        // First we setup i18n, then the locale, which will select the
        // locale for i18next as well as moment etc.
        setupI18next()
            .then(setupLocale)
            .then(locale => {
            const modernFamlyLocale = getModernFamlyLanguage(locale);
            setLocale(modernFamlyLocale);
            setSetupReady(true);
        });
        return () => {
            if (PlatformHelper.isSafariMobile()) {
                document.removeEventListener('touchend', () => null);
                document.removeEventListener('touchmove', () => null);
            }
        };
    }, []);
    // A reload discards the PIN session and everything typed into the sign-in flow, none of which
    // is persisted, so a new release waits for the tablet to be back on an idle screen. A guest
    // mid-way through signing in or out counts as busy too.
    useReloadOnNewRelease({
        canReload: () => !didInsertPin && !isCheckingIn && !addingNewRelation && !isOnGuestFlow,
    });
    // Refreshes the data regularly to stay synced with the main app.
    useInterval(() => {
        if (accessToken && !isPinApp) {
            dispatch(PrivateRouteActions.prefetch.action(accessToken));
        }
    }, 5 * MINUTE);
    if (!setupReady) {
        return null;
    }
    return (_jsx(ModernFamlyProvider, { theme: theme.mf, isMobileApp: PlatformHelper.isMobileApp(), language: locale, children: _jsx(ThemeProvider, { theme: theme, children: _jsx(AuthStateProvider, { value: { isLoggedIn }, children: _jsx(ModalDialogProvider, { children: _jsx(ModalContext, { children: _jsxs(AppContainer, { children: [_jsx(GlobalCSS, {}), _jsx(GlobalEvent, {}), _jsx(MFFonts, {}), _jsx(LegacyModalStyle, {}), _jsx(Routes, {}), _jsx("div", { id: PortalRootId.modalRoot })] }) }) }) }) }) }));
};
/*
|---------------------------------------------------------------------------------
| Helpers
|---------------------------------------------------------------------------------
*/
const disableDoubleClickZoom = () => {
    let lastTouchEnd = 0;
    document.addEventListener('touchend', event => {
        const now = new Date().getTime();
        if (now - lastTouchEnd <= 300) {
            event.preventDefault();
        }
        lastTouchEnd = now;
    }, false);
};
const disableTouchZoom = () => {
    document.addEventListener('touchmove', event => {
        if (event.scale !== 1) {
            event.preventDefault();
        }
    }, { passive: false });
};
export default App;
